import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';
import { queryClient } from 'index';
import { useState } from 'react';
import { approveWithdrawal } from 'services/api.service';
import { maskCurrency } from 'utils/currency';
import { Withdrawal } from '..';

interface ApproveWithdrawalProps {
  isOpen: boolean;
  onClose: () => void;
  withdrawal: Withdrawal;
}

export default function ApproveWithdrawal({ isOpen, onClose, withdrawal }: ApproveWithdrawalProps) {
  const [status, setStatus] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [observation, setObservation] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const result = await approveWithdrawal(withdrawal.id, {
        status,
        observation: observation || undefined,
      });

      toast({
        title: 'Sucesso!',
        description: result.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });

      queryClient.refetchQueries({ queryKey: ['pendingWithdrawalsForApproval'] });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao processar aprovação',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Aprovar/Rejeitar Saque</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack
            spacing={4}
            align="stretch"
          >
            <Box>
              <Text fontWeight="bold">Cliente:</Text>
              <Text>{withdrawal.Customer.name}</Text>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                Conta: {withdrawal.Customer.accountNumber}
              </Text>
            </Box>

            <Box>
              <Text fontWeight="bold">Valor do Saque:</Text>
              <Text
                fontSize="2xl"
                color="brand.500"
              >
                {maskCurrency(Number(withdrawal.amount))}
              </Text>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                Taxa: {maskCurrency(Number(withdrawal.fee))}
              </Text>
            </Box>

            {withdrawal.description && (
              <Box>
                <Text fontWeight="bold">Descrição:</Text>
                <Text>{withdrawal.description}</Text>
              </Box>
            )}

            {withdrawal.CustomerAccounts && (
              <Box>
                <Text fontWeight="bold">Dados Bancários:</Text>
                <Text fontSize="sm">
                  {withdrawal.CustomerAccounts.accountHolder}
                  <br />
                  PIX: {withdrawal.CustomerAccounts.pix || '-'}
                  <br />
                  Banco: {withdrawal.CustomerAccounts.bankIspb || '-'}
                  <br />
                  Ag: {withdrawal.CustomerAccounts.agency || '-'} / Conta: {withdrawal.CustomerAccounts.account || '-'}
                </Text>
              </Box>
            )}

            <FormControl isRequired>
              <FormLabel>Decisão</FormLabel>
              <RadioGroup
                value={status}
                onChange={(value) => setStatus(value as 'APPROVED' | 'REJECTED')}
              >
                <Stack
                  direction="row"
                  spacing={4}
                >
                  <Radio
                    value="APPROVED"
                    colorScheme="green"
                  >
                    Aprovar
                  </Radio>
                  <Radio
                    value="REJECTED"
                    colorScheme="red"
                  >
                    Rejeitar
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Observação (opcional)</FormLabel>
              <Textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Digite uma observação sobre esta decisão..."
                rows={4}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={loading}
          >
            Cancelar
          </Button>
          <CustomButton
            onClick={handleSubmit}
            isLoading={loading}
            colorScheme={status === 'APPROVED' ? 'green' : 'red'}
          >
            {status === 'APPROVED' ? 'Aprovar' : 'Rejeitar'}
          </CustomButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
